import { print } from "graphql";
import { Client } from "graphql-ws";
import localforage from "localforage";
import Router from "next/router";
import { getAuthCache } from "./use-auth-cache";
import { getOperationalNotificationsQueryKey } from "./use-get-operational-notifications";
import { getOrdersCache } from "./use-get-operational-orders";
import { resolveAssistanceMutationFn } from "./use-resolve-assistance";
import { toast } from "~served/ui-comps/toast";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { Notification } from "~/types/store";
import {
	GetOperationalNotificationsQuery,
	SubscribeToVenueNotificationsDocument,
	SubscribeToVenueNotificationsSubscription,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";
import { shouldNotifyForDigitalMenuLocation } from "~/utils/digital-menu-notification-locations";
import { getPaywayOrderFailureMessage } from "~/utils/payway-order-failure-message";

void (
	/* GraphQL */ `
		subscription SubscribeToVenueNotifications {
			subscribeToVenueNotifications {
				type
				id
				payload {
					_id
					created_at
					updated_at
					content
					staff
					location
					venue
					device
					kind
					_temp_order {
						_payment_amount
						_payment_currency
						_is_payment_approved
						_order_index
						failure_reason
					}
					_location {
						_id
						name
					}
				}
			}
		}
	`
);

export const subscribeToVenueNotifications = async (client: Client) => {
	const subscription =
		client.iterate<SubscribeToVenueNotificationsSubscription>({
			query: print(SubscribeToVenueNotificationsDocument),
		});
	for await (const result of subscription) {
		if (result.data) {
			const { type, payload } = result.data.subscribeToVenueNotifications;
			switch (type) {
				case "create": {
					if (!payload) break;
					const isPaywayFailure = payload.kind === "payway_order_failure";
					if (!isPaywayFailure) {
						const selectedLocationIds = await localforage.getItem<
							string[] | null
						>(STORAGE_KEYS.DIGITAL_MENU_NOTIFICATION_LOCATION_IDS);
						if (
							!shouldNotifyForDigitalMenuLocation(
								payload._location?._id ?? payload.location,
								selectedLocationIds,
							)
						)
							break;
					}
					queryClient.setQueryData<
						GqlResultOf<GetOperationalNotificationsQuery>
					>(getOperationalNotificationsQueryKey(), (prev) =>
						prev ? [...prev, payload] : [payload],
					);
					const newNotification: Notification = isPaywayFailure
						? {
								id: payload._id,
								message: getPaywayOrderFailureMessage(payload),
								type: "PAYWAY_ORDER_FAILURE",
								severity: "error",
							}
						: {
								id: payload._id,
								message: payload.content
									? `${payload._location.name}: "${payload.content}"`
									: `${payload._location.name} needs staff`,
								type: "STAFF_ASSISTANCE",
								severity: "info",
							};
					addNewNotification(newNotification);
					break;
				}
			}
		}
	}
};

export const addNewNotification = (notification: Notification) => {
	const { id, message, severity, type, customProps } = notification;

	const handleClose = async (notification: Notification) => {
		toast.dismiss(notification.id);

		const immediateAuth = getAuthCache();
		const immediateOrders = getOrdersCache();

		if (
			!!immediateAuth &&
			!!immediateOrders &&
			type === "NEW_ORDER" &&
			customProps?.status
		) {
			const isOrderStillInPlaced = immediateOrders.some(
				(o) => o._id === notification.id && o.status === "placed",
			);
			if (!isOrderStillInPlaced) return;
			if (Router && "push" in Router) {
				void Router.push("/ops/orders?id=" + notification.id);
			}
		}

		if (
			!!immediateAuth &&
			(notification.type === "STAFF_ASSISTANCE" ||
				notification.type === "PAYWAY_ORDER_FAILURE")
		) {
			await resolveAssistanceMutationFn({ id: notification.id });
		}
	};

	toast[severity](message, {
		id,
		position: "bottom-left",
		duration: Infinity,
		// The action both handles and dismisses the notification; a close button would skip it.
		closeButton: false,
		action: {
			label: type === "NEW_ORDER" ? "View" : "Resolve",
			onClick: (event) => {
				// handleClose dismisses the toast itself; keep it open when not logged in.
				event.preventDefault();
				if (getAuthCache()) void handleClose(notification);
				else toast.error("Login first", { id: "login-first", persist: false });
			},
		},
	});

	new Audio("/audios/ding_once.mp3").play().catch(console.error);
};
